import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Input } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faDoorOpen,
  faHouseCircleCheck,
  faLocationDot,
  faMagnifyingGlass,
  faPlus,
  faUserTie
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import { useProperties } from '../../queries/properties';
import { useProspects } from '../../queries/prospects';
import { useOwnerOptions } from '../../queries/owners';
import { useContactByName } from '../../queries/contacts';
import { profilePath } from '../../utils/contacts';
import { usePhone } from '../../hooks/usePhone';
import { isCommercial, isVacant, ownerRows, prospectsFor, unitStatus } from '../../utils/property';
import { HOME_TYPES, formatMoney, layoutOf } from '../Properties/constants';
import PropertyFormModal from '../Properties/PropertyFormModal';
import { AaTheme, Btn, Chip, I, PillSelect, Seg, cx } from '../Todos/ui';
import UnitDrawer, { StatusChip } from './UnitDrawer';
import { useSchedule } from '../Contacts/useSchedule';
import '../Todos/aa.css';
import '../../styles/mcc-pages.css';

const HOME_LABEL = Object.fromEntries(HOME_TYPES.map((t) => [t.value, t.label]));
const STATUS_FILTER = [
  ['all', 'All'],
  ['occupied', 'Occupied'],
  ['leased', 'Lease signed'],
  ['vacant', 'Vacant']
];

function Card({ title, icon, sub, right, sm, children }) {
  return (
    <section className={cx('m-card', sm && 'sm')} aria-label={title}>
      <div className="m-ch">
        <span className="m-bub">
          <I icon={icon} />
        </span>
        <div className="grow">
          <div className="m-ct">{title}</div>
          {sub && <div className="m-sub">{sub}</div>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function Table({ cols, rows, onRow, empty }) {
  return (
    <div className="m-tw">
      <table className="m-tbl">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((r, i) => (
              <tr key={r.key} className="click" tabIndex={0} onClick={() => onRow(i)} onKeyDown={(e) => e.key === 'Enter' && onRow(i)}>
                {r.cells.map((cell, j) => (
                  <td key={j}>{cell}</td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={cols.length} className="m-empty">
                {empty}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Overview({ units, phone, onFilter }) {
  const navigate = useNavigate();
  const vacant = units.filter(isVacant).length;
  const occ = units.length - vacant;
  const comm = units.filter(isCommercial).length;
  const owners = ownerRows(units);
  const view = (fn) => (
    <button type="button" className="aa-link sm" onClick={fn}>
      View
    </button>
  );
  const byName = useContactByName();
  const openOwner = (o) => {
    const c = o.ownerId ? { kind: 'owner', id: o.ownerId } : byName(o.name, ['owner']);
    if (c) navigate(profilePath(c));
  };
  return (
    <div className="m-col gap16">
      <div className={cx('m-four', phone && 'ph smallcards')}>
        <Card sm title="Doors managed" icon={faBuilding}>
          <div className="m-big num">{units.length}</div>
          <div className="m-sub">
            {units.length - comm} rentals · {comm} commercial and lots
          </div>
        </Card>
        <Card sm title="Occupied or leased" icon={faHouseCircleCheck} right={view(() => onFilter('rentals', 'occupied'))}>
          <div className="m-big num">{units.length ? Math.round((occ / units.length) * 100) : 0}%</div>
          <div className="m-sub">
            {occ} of {units.length} units
          </div>
        </Card>
        <Card sm title="Vacant" icon={faDoorOpen} right={view(() => onFilter('rentals', 'vacant'))}>
          <div className="m-big num">{vacant}</div>
          <div className="m-sub">Includes notice given</div>
        </Card>
      </div>
      <Card title="Owners" icon={faUserTie} sub="Units by owner">
        {phone ? (
          <div className="m-col gap8">
            {owners.map((o) => (
              <button key={o.name} type="button" className="m-crow" onClick={() => openOwner(o)}>
                <div className="m-row between">
                  <span className="b sm">{o.name}</span>
                  <span className="m-sub">{o.units} units</span>
                </div>
                <div className="m-sub">
                  {o.vacant} vacant · {formatMoney(o.rent)} monthly rent
                </div>
              </button>
            ))}
          </div>
        ) : (
          <Table
            cols={['Owner', 'Units', 'Vacant', 'Monthly rent', 'Open work orders']}
            rows={owners.map((o) => ({
              key: o.name,
              cells: [
                <span className="b">{o.name}</span>,
                String(o.units),
                o.vacant ? (
                  <Chip fam="warn" icon={faDoorOpen}>
                    {o.vacant}
                  </Chip>
                ) : (
                  '0'
                ),
                <span className="b num">{formatMoney(o.rent)}</span>,
                String(o.work)
              ]
            }))}
            onRow={(i) => openOwner(owners[i])}
            empty="No units yet."
          />
        )}
      </Card>
    </div>
  );
}

function Rentals({ units, comm, phone, status, onStatus, canManage, onOpen, onAdd }) {
  const navigate = useNavigate();
  const { rows: prospects } = useProspects();
  const [q, setQ] = useState('');
  const [area, setArea] = useState('all');
  const all = units.filter((u) => (comm ? isCommercial(u) : !isCommercial(u)));
  const areas = [...new Set(all.map((u) => u.area || u.address?.city).filter(Boolean))].sort();
  const needle = q.trim().toLowerCase();
  const rows = all.filter((u) => {
    const s = unitStatus(u);
    if (status !== 'all' && (status === 'vacant' ? !isVacant(u) : s !== status)) return false;
    if (area !== 'all' && (u.area || u.address?.city) !== area) return false;
    return !needle || `${u.name} ${u.ownerName || ''} ${u.tenantName || ''}`.toLowerCase().includes(needle);
  });
  const byName = useContactByName();
  const tenant = (u) => {
    const c = u.tenantId ? { kind: 'client', id: u.tenantId } : byName(u.tenantName, ['tenant']);
    return c ? (
      <button
        type="button"
        className="aa-link"
        onClick={(e) => {
          e.stopPropagation();
          navigate(profilePath(c));
        }}
      >
        {u.tenantName}
      </button>
    ) : (
      <span className={u.tenantName ? null : 'm-sub'}>{u.tenantName || 'None'}</span>
    );
  };
  const pros = (u) => {
    const n = prospectsFor(prospects, u.name).length;
    return n ? `${n} in pipeline` : 'None yet';
  };
  const bb = (u) => (u.bedrooms ? `${u.bedrooms} / ${u.bathrooms || 0}` : u.unitType === 'room' ? 'Room' : '--');
  const areaOf = (u) => u.area || u.address?.city || '--';
  const tools = (
    <div className="m-row wrap">
      <Input
        className="aa-search"
        style={phone ? undefined : { maxWidth: 240 }}
        allowClear
        prefix={<FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: '#8C8C8C' }} />}
        placeholder="Search address, tenant or owner"
        aria-label="Search property"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <Seg items={STATUS_FILTER} value={status} onChange={onStatus} label="Status" />
      <PillSelect
        aria-label="Area"
        icon={faLocationDot}
        tip="Show only one area"
        value={area}
        onChange={setArea}
        options={[{ value: 'all', label: 'All areas' }, ...areas.map((a) => ({ value: a, label: a }))]}
      />
      <div className="grow" />
      {canManage && (
        <Btn kind="primary" sm icon={faPlus} onClick={onAdd}>
          {comm ? 'Add commercial space' : 'Add property'}
        </Btn>
      )}
    </div>
  );
  const body = phone ? (
    <div className="m-col gap8">
      {rows.map((u) => (
        <button key={u._id} type="button" className="m-crow" onClick={() => onOpen(u)}>
          <div className="m-row between">
            <span className="b sm">{u.name}</span>
            <StatusChip unit={u} />
          </div>
          <div className="m-sub">{[u.area, layoutOf(u), formatMoney(u.rent)].filter(Boolean).join(' · ')}</div>
          <div className="m-sub">
            {u.tenantName ? `Tenant ${u.tenantName} · ` : ''}Owner {u.ownerName || '--'}
          </div>
        </button>
      ))}
    </div>
  ) : (
    <Table
      cols={
        comm
          ? ['Space', 'Area', 'Type', 'Asking rent', 'Status', 'Prospects', 'Owner']
          : ['Property or unit', 'Area', 'Type', 'Beds / baths', 'Rent', 'Status', 'Tenant', 'Owner']
      }
      rows={rows.map((u) => ({
        key: u._id,
        cells: comm
          ? [<span className="b">{u.name}</span>, areaOf(u), layoutOf(u), <span className="b num">{formatMoney(u.rent) || '--'}</span>, <StatusChip unit={u} />, pros(u), u.ownerName || '--']
          : [
              <span className="b">{u.name}</span>,
              areaOf(u),
              HOME_LABEL[u.unitType] || '--',
              <span className="num">{bb(u)}</span>,
              <span className="b num">{formatMoney(u.rent) || '--'}</span>,
              <StatusChip unit={u} />,
              tenant(u),
              u.ownerName || '--'
            ]
      }))}
      onRow={(i) => onOpen(rows[i])}
      empty="No property matches. Clear the search or filters."
    />
  );
  return (
    <div className="m-col gap12">
      {tools}
      <div className="m-sub">
        {rows.length} of {all.length} {comm ? 'commercial spaces and lots' : 'rental units'} · select a row for details
      </div>
      {body}
    </div>
  );
}

export default function Property() {
  const phone = usePhone();
  const { can } = useAuth();
  const { state } = useLocation();
  const { rows: units, isLoading } = useProperties();
  const owners = useOwnerOptions();
  const [tab, setTab] = useState('overview');
  const [status, setStatus] = useState('all');
  const [openId, setOpenId] = useState(state?.unitId || null);
  const [form, setForm] = useState(null);
  const [schedule, scheduleNode] = useSchedule();
  const canManage = can('manage_listings');
  const open = units.find((u) => u._id === openId);
  const comm = units.filter(isCommercial).length;
  const filter = (t, s) => {
    setTab(t);
    setStatus(s);
  };

  return (
    <AaTheme>
      <div className={cx('aa', phone && 'ph')}>
        <div className="m-page">
          <div className="m-ptitle">
            <div className="grow">
              <h1>Property</h1>
              <div className="m-sub">Every door: rentals, commercial space and lots</div>
            </div>
          </div>
          <div className="aa-types">
            <Seg
              items={[
                ['overview', 'Overview'],
                ['rentals', 'Rentals', null, units.length - comm],
                ['commercial', 'Commercial & lots', null, comm]
              ]}
              value={tab}
              onChange={setTab}
              label="Property section"
            />
          </div>
          {isLoading ? (
            <div className="m-emptyc">
              <div className="b">Loading property…</div>
            </div>
          ) : tab === 'overview' ? (
            <Overview units={units} phone={phone} onFilter={filter} />
          ) : (
            <Rentals
              key={tab}
              units={units}
              comm={tab === 'commercial'}
              phone={phone}
              status={status}
              onStatus={setStatus}
              canManage={canManage}
              onOpen={(u) => setOpenId(u._id)}
              onAdd={() => setForm(tab === 'commercial' ? { use: 'commercial', unitType: 'office' } : {})}
            />
          )}
        </div>
      </div>
      {open && (
        <UnitDrawer
          unit={open}
          canManage={canManage}
          onEdit={() => {
            setOpenId(null);
            setForm(open);
          }}
          onSchedule={(kind, initial) => {
            setOpenId(null);
            schedule(kind, initial);
          }}
          onClose={() => setOpenId(null)}
        />
      )}
      {scheduleNode}
      {form && <PropertyFormModal listing={form} owners={owners} onClose={() => setForm(null)} />}
    </AaTheme>
  );
}
